import { useEffect, useRef } from 'react';

import { subscribeSocketEvents, unsubscribeSocketEvents } from './socket';

import { useLiveRef } from '../useLiveRef';

type SetTimeoutReturn = ReturnType<typeof setTimeout>;

/**
 * Subscribe to socket events data and debounce the callback to handle changes at once
 *
 * @param socketEvents Event names we want to subscribe to
 * @param callback Callback to handle the array of datas
 * @param delay
 */
export const useSocketEventsDebounced = (
  socketEvents: string[],
  callback: (ids: any[]) => void,
  delay: number | undefined = 500,
) => {
  const array = useRef<any[]>([]);
  const timerRef = useRef<SetTimeoutReturn>();
  // Avoid passing unstable reference values into
  // `useEffect` dependency array.
  const onSocketCallback = useLiveRef(callback);
  const socketEventsString = socketEvents.join(',');

  useEffect(() => {
    const $socketEvents = socketEventsString
      ? socketEventsString.split(',')
      : [];
    const onSocket = (data: any = {}) => {
      clearTimeout(timerRef.current as SetTimeoutReturn);
      array.current.push(data);

      timerRef.current = setTimeout(() => {
        onSocketCallback.current([...array.current]);
        array.current.splice(0, array.current.length);
      }, delay);
    };
    subscribeSocketEvents($socketEvents, onSocket);
    return () => {
      unsubscribeSocketEvents($socketEvents, onSocket);
    };
  }, [socketEventsString, onSocketCallback, delay]);
};
